import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Calendar, MapPin, Users, ArrowLeft, Mic2, Trash2, Music, Radio, Star, Disc3,
  Send, MessageSquare, Clock, Share2, ExternalLink, ListMusic, Download,
  DollarSign, Ticket, QrCode, ShoppingCart, Settings,
} from "lucide-react";

import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  concert: { icon: Music, label: "Concert", color: "from-[#FF6B35] to-[#FF8FA3]" },
  live: { icon: Radio, label: "Live", color: "from-[#7000FF] to-[#FF6B35]" },
  meetup: { icon: Users, label: "Meetup", color: "from-emerald-500 to-teal-400" },
  release: { icon: Disc3, label: "Sortie", color: "from-[#FF8FA3] to-[#7000FF]" },
  other: { icon: Star, label: "Autre", color: "from-[#6E6680] to-[#AFA8BA]" },
};

function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!targetDate) return;
    const target = new Date(targetDate).getTime();

    const tick = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ days, hours, mins, secs, total: diff });
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

export default function EventDetailPage() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();

  // Get referral code from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  const referralCode = urlParams.get("ref");

  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");
  const [eventPosts, setEventPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [postLoading, setPostLoading] = useState(false);
  const [ticketCats, setTicketCats] = useState([]);
  const [buyingCat, setBuyingCat] = useState(null);
  const [buyQuantity, setBuyQuantity] = useState(1);
  const [buyLoading, setBuyLoading] = useState(false);

  const countdown = useCountdown(event?.date);

  const fetchEvent = useCallback(async () => {
    try {
      const res = await api.get(`/events/${id}`);
      setEvent(res.data.event);
      setAttendees(res.data.attendees);
    } catch {
      navigate("/events");
    }
  }, [api, id, navigate]);

  const fetchEventPosts = useCallback(async () => {
    try {
      const res = await api.get(`/events/${id}/posts`, { params: { limit: 50 } });
      setEventPosts(res.data.posts);
    } catch (err) { console.error(err); }
  }, [api, id]);

  const fetchTicketCats = useCallback(async () => {
    try {
      const res = await api.get(`/tickets/events/${id}/categories`);
      setTicketCats(res.data.categories || []);
    } catch (err) { console.error(err); }
  }, [api, id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchEvent(), fetchEventPosts(), fetchTicketCats()]).finally(() => setLoading(false));
  }, [fetchEvent, fetchEventPosts]);

  const handleAttend = async () => {
    try {
      const res = await api.post(`/events/${id}/attend`);
      setEvent((e) => ({ ...e, is_attending: res.data.attending, attendee_count: res.data.attendee_count }));
      if (res.data.attending) fetchEvent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handlePost = async () => {
    if (!newPost.trim()) return;
    setPostLoading(true);
    try {
      const res = await api.post(`/events/${id}/posts`, { content: newPost });
      setEventPosts((prev) => [res.data.post, ...prev]);
      setNewPost("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setPostLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer cet evenement ?")) return;
    try {
      await api.delete(`/events/${id}`);
      navigate("/events");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleShare = async () => {
    const refCode = user?.id || "";
    const url = `${window.location.origin}/events/${id}${refCode ? `?ref=${refCode}` : ""}`;
    const text = `${event.title} par ${event.artist_name} sur ReallyFan`;
    if (navigator.share) {
      try { await navigator.share({ title: event.title, text, url }); } catch (err) { console.error(err); }
    } else {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Lien de parrainage copie ! Partagez-le pour gagner des credits.");
    }
  };

  const exportCalendar = () => {
    window.open(`${API_URL}/api/events/${id}/ics`, "_blank");
  };

  if (loading) {
    return (
      <div className="py-6 space-y-4">
        <div className="bg-card rounded-3xl h-48 animate-pulse" />
      </div>
    );
  }

  if (!event) return null;

  const config = TYPE_CONFIG[event.event_type] || TYPE_CONFIG.other;
  const TypeIcon = config.icon;
  const isCreator = event.creator_user_id === user?.id;

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="py-6 space-y-6" data-testid="event-detail-page">
      <button
        onClick={() => navigate("/events")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-card-foreground transition-colors"
        data-testid="event-back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Evenements
      </button>

      {/* Event header */}
      <div className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm">
        {event.cover_url ? (
          <div className="h-48 relative">
            <img src={event.cover_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          </div>
        ) : (
          <div className={`h-28 bg-gradient-to-br ${config.color} opacity-30`} />
        )}
        <div className={`px-5 pb-6 ${event.cover_url ? "-mt-16" : "-mt-12"}`}>
          <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${config.color} flex items-center justify-center border-4 border-background shadow-xl`}>
            <TypeIcon className="w-8 h-8 text-white" />
          </div>

          <div className="mt-4 flex items-start justify-between">
            <div className="flex-1">
              <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r ${config.color} text-white mb-2`}>
                <TypeIcon className="w-3 h-3" />
                {config.label}
              </span>
              <h1 className="font-heading font-extrabold text-2xl text-card-foreground">{event.title}</h1>

              {/* Countdown */}
              {countdown && (
                <div className="mt-3 flex gap-3" data-testid="event-countdown">
                  {[
                    { val: countdown.days, label: "jours" },
                    { val: countdown.hours, label: "heures" },
                    { val: countdown.mins, label: "min" },
                    { val: countdown.secs, label: "sec" },
                  ].map((item) => (
                    <div key={item.label} className="bg-muted rounded-xl px-3 py-2 text-center min-w-[52px] border border-border">
                      <p className="font-heading font-extrabold text-lg text-[#FF6B35]">{item.val}</p>
                      <p className="text-[9px] text-muted-foreground uppercase">{item.label}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-4 mt-3">
                <span className="flex items-center gap-1.5 text-sm text-card-foreground/80">
                  <Calendar className="w-4 h-4 text-[#FF6B35]" />
                  {formatDate(event.date)}
                  {event.time && <span className="text-[#FF6B35] font-semibold ml-1">{event.time}</span>}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-card-foreground/80">
                  <Mic2 className="w-4 h-4 text-[#FF8FA3]" />
                  {event.artist_name}
                </span>
                {event.location && (
                  <span className="flex items-center gap-1.5 text-sm text-card-foreground/80">
                    <MapPin className="w-4 h-4 text-[#7000FF]" />
                    {event.location}
                  </span>
                )}
                <span className="flex items-center gap-1.5 text-sm text-card-foreground/80">
                  <Users className="w-4 h-4 text-emerald-400" />
                  {event.attendee_count} participant{event.attendee_count > 1 ? "s" : ""}
                  {event.max_attendees ? ` / ${event.max_attendees} places` : ""}
                </span>
              </div>

              {/* Ticket info */}
              {(event.ticket_price != null || event.ticket_url) && (
                <div className="flex items-center gap-3 mt-3">
                  {event.ticket_price != null && (
                    <span className="flex items-center gap-1 text-sm font-semibold text-[#FF6B35] bg-[#FF6B35]/10 px-3 py-1 rounded-full">
                      <Ticket className="w-4 h-4" />
                      {event.ticket_price === 0 ? "Gratuit" : `${event.ticket_price} EUR`}
                    </span>
                  )}
                  {event.ticket_url && (
                    <a
                      href={event.ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-[#7000FF] hover:underline"
                      data-testid="event-ticket-link"
                    >
                      <ExternalLink className="w-3 h-3" /> Billetterie
                    </a>
                  )}
                </div>
              )}

              {event.address && (
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {event.address}
                </p>
              )}

              {event.description && (
                <p className="text-sm text-card-foreground/70 mt-4 leading-relaxed whitespace-pre-wrap">{event.description}</p>
              )}
            </div>

            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={handleShare} className="p-2 rounded-xl bg-muted text-muted-foreground hover:text-[#FF6B35] transition-colors" data-testid="event-share-btn">
                <Share2 className="w-4 h-4" />
              </button>
              <button onClick={exportCalendar} className="p-2 rounded-xl bg-muted text-muted-foreground hover:text-[#FF6B35] transition-colors" data-testid="event-calendar-btn" title="Exporter vers le calendrier">
                <Download className="w-4 h-4" />
              </button>
              {isCreator && (
                <button onClick={handleDelete} className="p-2 rounded-xl bg-muted text-muted-foreground hover:text-red-400 transition-colors" data-testid="event-delete-btn">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="mt-5 flex gap-3 flex-wrap">
            <button
              onClick={handleAttend}
              className={`font-bold px-8 py-3 rounded-full text-sm transition-all duration-200 ${
                event.is_attending
                  ? "bg-muted text-muted-foreground border border-border hover:bg-muted/80"
                  : "bg-[#FF6B35] text-white shadow-[0_0_20px_rgba(255,107,53,0.4)] hover:scale-105 active:scale-95"
              }`}
              data-testid="event-attend-btn"
            >
              {event.is_attending ? "Annuler mon inscription" : "Je participe"}
            </button>
            {isCreator && (
              <>
                <button
                  onClick={() => navigate(`/events/${id}/tickets`)}
                  className="font-bold px-6 py-3 rounded-full text-sm bg-card border border-border text-card-foreground hover:border-[#FF6B35]/40 transition-colors flex items-center gap-1.5"
                  data-testid="manage-tickets-btn"
                >
                  <Settings className="w-4 h-4" /> Gerer les billets
                </button>
                <button
                  onClick={() => navigate(`/events/${id}/scanner`)}
                  className="font-bold px-6 py-3 rounded-full text-sm bg-card border border-border text-card-foreground hover:border-[#FF6B35]/40 transition-colors flex items-center gap-1.5"
                  data-testid="scanner-btn"
                >
                  <QrCode className="w-4 h-4" /> Scanner
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-card rounded-full p-1 border border-border">
        {[
          { key: "info", icon: ListMusic, label: "Details" },
          ...(ticketCats.length > 0 ? [{ key: "tickets", icon: Ticket, label: "Billets" }] : []),
          { key: "feed", icon: MessageSquare, label: "Fil en direct" },
          { key: "participants", icon: Users, label: `Participants (${event.attendee_count})` },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
              tab === t.key ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
            }`}
            data-testid={`event-tab-${t.key}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <div className="space-y-4">
          {/* Setlist */}
          {event.setlist?.length > 0 && (
            <div className="bg-card border border-border rounded-3xl p-5 shadow-sm" data-testid="event-setlist">
              <h3 className="font-heading font-bold text-sm text-card-foreground mb-3 flex items-center gap-2">
                <ListMusic className="w-4 h-4 text-[#FF6B35]" />
                Setlist
              </h3>
              <div className="space-y-1.5">
                {event.setlist.map((track, i) => (
                  <div key={`setlist-${i}-${track}`} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <span className="text-xs text-muted-foreground font-mono w-5 text-right">{i + 1}</span>
                    <span className="text-sm text-card-foreground">{track}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!event.setlist?.length && (
            <div className="text-center py-10">
              <ListMusic className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Aucun detail supplementaire</p>
            </div>
          )}
        </div>
      )}

      {tab === "tickets" && (
        <div className="space-y-3" data-testid="tickets-tab">
          <h3 className="font-heading font-bold text-sm text-card-foreground flex items-center gap-2">
            <Ticket className="w-4 h-4 text-[#FF6B35]" />
            Choisissez votre billet
          </h3>
          {ticketCats.map((cat) => {
            const available = cat.quantity - cat.sold;
            const isSoldOut = available <= 0;
            return (
              <div
                key={cat.id}
                className={`bg-card border rounded-2xl p-4 transition-all ${
                  buyingCat === cat.id ? "border-[#FF6B35] shadow-[0_0_15px_rgba(255,107,53,0.15)]" : "border-border"
                } ${isSoldOut ? "opacity-60" : "cursor-pointer hover:border-[#FF6B35]/30"}`}
                onClick={() => !isSoldOut && setBuyingCat(buyingCat === cat.id ? null : cat.id)}
                data-testid={`ticket-cat-${cat.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-card-foreground">{cat.name}</p>
                    {cat.description && <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{available} place{available > 1 ? "s" : ""} restante{available > 1 ? "s" : ""}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-extrabold text-lg text-[#FF6B35]">
                      {cat.price === 0 ? "Gratuit" : `${(cat.price / 100).toFixed(2)} EUR`}
                    </p>
                    {cat.price > 0 && <p className="text-[10px] text-muted-foreground">+ 1 EUR frais</p>}
                    {isSoldOut && <span className="text-[10px] font-bold text-red-400">COMPLET</span>}
                  </div>
                </div>
                {buyingCat === cat.id && !isSoldOut && (
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setBuyQuantity(Math.max(1, buyQuantity - 1)); }}
                        className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-card-foreground font-bold"
                      >-</button>
                      <span className="font-heading font-bold text-lg text-card-foreground w-8 text-center">{buyQuantity}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setBuyQuantity(Math.min(Math.min(10, available), buyQuantity + 1)); }}
                        className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-card-foreground font-bold"
                      >+</button>
                    </div>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        setBuyLoading(true);
                        try {
                          const res = await api.post(`/tickets/events/${id}/purchase`, {
                            category_id: cat.id, quantity: buyQuantity,
                            ...(referralCode ? { referral_code: referralCode } : {}),
                          });
                          toast.success(`${buyQuantity} billet(s) achete(s) ! Retrouvez-les dans "Mes Billets".`);
                          setBuyingCat(null);
                          setBuyQuantity(1);
                          fetchTicketCats();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Erreur d'achat");
                        }
                        setBuyLoading(false);
                      }}
                      disabled={buyLoading}
                      className="bg-[#FF6B35] text-white font-bold px-5 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`buy-btn-${cat.id}`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {buyLoading ? "..." : cat.price === 0 ? "Reserver" : `${(((cat.price + 100) * buyQuantity) / 100).toFixed(2)} EUR`}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-center mt-2">
            <button
              onClick={() => navigate("/my-tickets")}
              className="text-xs text-[#FF6B35] hover:underline flex items-center gap-1"
            >
              <Ticket className="w-3 h-3" /> Voir mes billets
            </button>
          </div>

          {/* Referral sharing banner */}
          {user && (
            <div className="bg-gradient-to-r from-[#FF6B35]/10 to-[#7000FF]/10 border border-[#FF6B35]/30 rounded-2xl p-4 flex items-center gap-3" data-testid="referral-banner">
              <div className="w-10 h-10 rounded-full bg-[#FF6B35]/20 flex items-center justify-center shrink-0">
                <Share2 className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <div className="flex-1">
                <p className="font-heading font-bold text-sm text-card-foreground">Partage & gagne des credits</p>
                <p className="text-[10px] text-muted-foreground">5% du prix des billets achetes via ton lien</p>
              </div>
              <button
                onClick={handleShare}
                className="bg-[#FF6B35] text-white font-bold px-4 py-2 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform shrink-0"
                data-testid="referral-share-btn"
              >
                Partager
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "feed" && (
        <div className="space-y-4">
          {(event.is_attending || isCreator) && (
            <div className="bg-card border border-border rounded-3xl p-4 shadow-sm" data-testid="event-post-form">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                  <span className="text-white text-xs font-bold">
                    {(user?.pseudo || "?")[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <textarea
                    placeholder="Partage ton excitation..."
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none h-16"
                    data-testid="event-post-input"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handlePost}
                      disabled={postLoading || !newPost.trim()}
                      className="bg-[#FF6B35] text-white font-bold px-4 py-1.5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="event-post-submit"
                    >
                      <Send className="w-3 h-3" />
                      {postLoading ? "..." : "Publier"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {eventPosts.length === 0 ? (
            <div className="text-center py-10">
              <MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Aucun message pour le moment</p>
            </div>
          ) : (
            eventPosts.map((p) => (
              <div key={p.id} className="bg-card border border-border rounded-2xl p-4 shadow-sm" data-testid={`event-post-${p.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                    {p.user_avatar ? (
                      <img src={p.user_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-white text-xs font-bold">
                        {(p.user_pseudo || "?")[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-semibold text-sm text-card-foreground">{p.user_pseudo}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(p.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <p className="text-sm text-card-foreground/80 mt-0.5 whitespace-pre-wrap">{p.content}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "participants" && (
        <div className="bg-card border border-border rounded-3xl p-5 shadow-sm">
          {attendees.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Aucun participant pour le moment</p>
          ) : (
            <div className="space-y-2">
              {attendees.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/user/${a.user_id}`)}
                  data-testid={`attendee-${a.user_id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                    {a.avatar_url ? (
                      <img src={a.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-white text-xs font-bold">
                        {(a.pseudo || "?")[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-sm text-card-foreground">{a.pseudo}</p>
                    <p className="text-[10px] text-muted-foreground">
                      Inscrit le {new Date(a.joined_at).toLocaleDateString("fr-FR")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
